import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';

export default function About() {
  const [formStatus, setFormStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: null, message: '' });

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
      setSubmitting(false);
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      honeypotEl && formData.delete('website_alt');

      const response = await fetch(
        'https://readdy.ai/api/form/d9pf099n2trhb35gve80',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
        form.reset();
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string } }).meta?.message ||
          (parsed as { message?: string }).message ||
          (parsed as { meta?: { detail?: string } }).meta?.detail ||
          responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus({ type: 'error', message: '提交失败，请稍后重试。' });
        } else {
          setFormStatus({
            type: 'error',
            message: typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。',
          });
        }
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请检查网络后重试。' });
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫网',
    url: 'https://www.hanmanwang.com/about',
    description:
      '韩漫网是一个免费韩漫在线阅读平台，提供高清下拉式韩漫全集阅读服务，致力于为韩漫爱好者提供极致的在线阅读体验。',
    about: {
      '@type': 'Organization',
      name: '韩漫网',
      url: 'https://www.hanmanwang.com',
      description:
        '韩漫网是您的免费韩漫在线阅读平台，提供高清下拉式全集漫画大全。',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'contact@hanmanwang.com',
        contactType: 'customer service',
        availableLanguage: 'Chinese',
      },
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <PageSEO
        title="关于我们_韩漫网 - 免费韩漫在线阅读平台"
        description="韩漫网是一个免费韩漫在线阅读平台，提供高清下拉式韩漫全集阅读服务，画质清晰阅读流畅，致力于为韩漫爱好者提供极致的在线阅读体验。"
        keywords="韩漫网,关于我们,免费韩漫,漫画平台,在线阅读"
        canonicalUrl="https://www.hanmanwang.com/about"
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Hero */}
        <section className="relative w-full h-[280px] md:h-[350px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cozy%20reading%20corner%20with%20bookshelf%20and%20warm%20lamp%20light%2C%20comfortable%20armchair%2C%20soft%20natural%20light%20from%20window%2C%20peaceful%20atmosphere%2C%20literary%20theme%2C%20warm%20neutral%20tones%2C%20artistic%20photography%20style%20with%20shallow%20depth%20of%20field&width=1600&height=600&seq=about-hero-01&orientation=landscape"
            alt="关于韩漫网"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/3"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-3">
                关于韩漫网
              </h1>
              <p className="text-sm md:text-base text-background-200 max-w-2xl">
                致力于为韩漫爱好者打造极致的在线阅读体验
              </p>
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6 text-center">
              <strong>品牌介绍</strong>
            </h2>
            <div className="space-y-4">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                韩漫网成立于2020年，是国内领先的<strong>免费韩漫在线阅读平台</strong>。我们致力于为广大韩漫爱好者提供<strong>高清</strong>、流畅的漫画阅读体验，让每一位读者都能在这里找到自己喜爱的作品。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                平台采用先进的<strong>下拉式阅读</strong>方式，完美适配手机、平板和电脑等多种设备。我们精心挑选了数千部优质韩漫作品，涵盖恋爱、奇幻、热血、悬疑、日常、古风等多种题材，满足不同读者的阅读需求。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                我们的团队由一群热爱漫画的年轻人组成，始终坚持<strong>完全免费</strong>的原则，让每一位读者都能无门槛地享受阅读的乐趣。同时，我们与多家韩国漫画平台保持合作关系，确保内容的及时更新和正版授权。
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              <strong>我们的优势</strong>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-image-line',
                  title: '超清画质',
                  desc: '所有漫画均采用高分辨率图片，确保细节清晰可见，带给您极致的视觉享受。',
                },
                {
                  icon: 'ri-speed-up-line',
                  title: '流畅阅读',
                  desc: '下拉式阅读设计配合智能预加载技术，翻页零等待，沉浸在漫画世界中。',
                },
                {
                  icon: 'ri-device-line',
                  title: '多端适配',
                  desc: '完美支持手机、平板、电脑等多种设备，随时随地畅享阅读。',
                },
                {
                  icon: 'ri-money-cny-circle-line',
                  title: '完全免费',
                  desc: '坚持免费原则，无需任何付费或订阅，所有漫画无条件免费阅读。',
                },
                {
                  icon: 'ri-refresh-line',
                  title: '及时更新',
                  desc: '与官方保持同步更新，热门连载漫画24小时内上线，追更不等待。',
                },
                {
                  icon: 'ri-shield-user-line',
                  title: '正版授权',
                  desc: '重视版权保护，所有内容均获得正版授权，为创作者提供支持。',
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-lg bg-background-50">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-lg text-primary-500`}></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold font-heading text-foreground-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              <strong>用户口碑</strong>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '小雨',
                  avatar: 'https://readdy.ai/api/search-image?query=Cute%20anime%20style%20girl%20avatar%20with%20pink%20hair%2C%20simple%20flat%20illustration%2C%20clean%20background%2C%20minimalist%20design&width=100&height=100&seq=avatar-01&orientation=squarish',
                  text: '韩漫网真的是宝藏平台！画质超清晰，下拉式阅读体验太舒服了。已经推荐给身边所有喜欢看韩漫的朋友了！',
                  rating: 5,
                },
                {
                  name: '星辰大海',
                  avatar: 'https://readdy.ai/api/search-image?query=Cool%20anime%20style%20boy%20avatar%20with%20blue%20hair%2C%20simple%20flat%20illustration%2C%20clean%20background%2C%20minimalist%20design&width=100&height=100&seq=avatar-02&orientation=squarish',
                  text: '追了三年韩漫了，韩漫网是我用过最好用的平台。更新快，分类全，而且完全免费，真心好评！',
                  rating: 5,
                },
                {
                  name: '樱花飘落',
                  avatar: 'https://readdy.ai/api/search-image?query=Pretty%20anime%20style%20girl%20avatar%20with%20long%20black%20hair%2C%20simple%20flat%20illustration%2C%20clean%20background%2C%20minimalist%20design&width=100&height=100&seq=avatar-03&orientation=squarish',
                  text: '手机上看特别方便，界面简洁好看。最喜欢恋爱和古风分类，每一部都追得停不下来！',
                  rating: 5,
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-background-100 rounded-lg p-5 flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-800">
                        {item.name}
                      </p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: item.rating }).map((_, j) => (
                          <div key={j} className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px] text-primary-500"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                    &ldquo;{item.text}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3 text-center">
              <strong>联系我们</strong>
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如有任何问题或建议，欢迎通过以下表单与我们联系，我们会尽快回复。
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="contact-subject"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label
                  htmlFor="contact-message"
                  className="block text-sm font-medium text-foreground-700 mb-1.5"
                >
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                />
                <p className="text-xs text-foreground-400 mt-1">最多输入500个字符</p>
              </div>

              {/* Status */}
              {formStatus.type && (
                <div
                  className={`px-4 py-3 rounded-lg text-sm ${
                    formStatus.type === 'success'
                      ? 'bg-accent-100 text-accent-800'
                      : 'bg-primary-100 text-primary-800'
                  }`}
                >
                  {formStatus.message}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {submitting ? '提交中...' : '发送留言'}
              </button>
            </form>
          </div>
        </section>

        {/* Contact Info */}
        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              <strong>联系方式</strong>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                {
                  icon: 'ri-mail-line',
                  title: '电子邮箱',
                  content: 'contact@hanmanwang.com',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: '在线客服',
                  content: '工作日 9:00 - 18:00',
                },
                {
                  icon: 'ri-map-pin-line',
                  title: '公司地址',
                  content: '中国上海市浦东新区张江高科技园区',
                },
              ].map((item, i) => (
                <div key={i} className="text-center p-6 rounded-lg bg-background-100">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-sm font-bold font-heading text-foreground-800 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}